import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  Box,
  Alert,
  AlertDescription,
  AlertIcon,
} from '@chakra-ui/react';

import { MdCancel } from 'react-icons/md';
import { useMemo, useState } from 'react';
import { stagesList } from '@/constants/contact-stages';
import { SanitizedText } from '@/ui/SanitizedDom';
import { useGetContactRejectionReasonQuery } from '@/store/query/contacts';
import { Formik, Form, Field } from 'formik';
import { FormSelect } from '@/components/form/FormSelect';
import { rejectionStage } from '@/constants/contact-stages';
import {
  useMoveStageMutation,
  useRejectStageMutation,
} from '@/store/query/contacts';
import * as Yup from 'yup';

const stageRejectionSchema = Yup.object().shape({
  reasonId: Yup.object().required('Rejection reason is required'),
});

const getConfirmationText = ({ fromStage, candidate, targetStage }) => {
  if (stagesList[fromStage]?.label) {
    return `Are you sure to move <b>${
      candidate?.full_name || candidate?.first_name
    }</b> from <b>${stagesList[fromStage]?.label}</b> to <b>${
      targetStage?.label
    }</b>?`;
  }
  return `Are you sure to move <b>${
    candidate?.full_name || candidate?.first_name
  }</b>  to <b>${targetStage?.label}</b>?`;
};

const RejectStage = ({
  candidate,
  fromStage,
  targetStage,
  onSaveSuccess = () => {},
  onClose = () => {},
}) => {
  const toast = useToast();
  const [error, setError] = useState(null);
  const [rejectStage, { isLoading }] = useRejectStageMutation();
  const { data, isLoading: isRejectionReasonLoading } =
    useGetContactRejectionReasonQuery({
      contactId: candidate?.id,
    });

  const onRejectStage = async (values) => {
    try {
      const result = await rejectStage({
        ids: [candidate?.id],
        stage: targetStage?.id,
        ...values,
      });
      const updateStageError = result?.data?.[0]?.errors;
      // ATS Error
      if (updateStageError?.contact?.base?.length) {
        setError(updateStageError?.contact?.base[0]);
        return;
      }
      if (updateStageError?.length || updateStageError?.contact) {
        toast({
          title: 'Contact stage update failed',
          description: updateStageError[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        onClose();
        return;
      }
      toast({
        title: 'Contact stage updated',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
      onClose();
      onSaveSuccess();
    } catch {}
  };

  const validationSchema = useMemo(() => {
    let schema = stageRejectionSchema;
    if (!data?.rejection_reasons?.length) {
      schema = schema.omit(['reasonId']);
    }
    return schema;
  }, [data]);

  return (
    <>
      <ModalHeader>
        <Text size="lg-bold">Update Stage</Text>
        <SanitizedText
          size="sm"
          fontWeight="400"
          variant="muted"
          html={getConfirmationText({ candidate, fromStage, targetStage })}
        />
      </ModalHeader>
      <ModalBody>
        {error && (
          <Alert
            mb="12px"
            status="error"
            borderRadius="6px"
            bg="#FFF1F1"
            border="1px solid #DA1E28"
          >
            <AlertIcon as={MdCancel} w="24px" h="24px" />
            <AlertDescription fontWeight="400" fontSize="14px">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {!isRejectionReasonLoading && (
          <Formik
            validateOnBlur={false}
            initialValues={{
              reasonId: '',
              note: '',
            }}
            validationSchema={validationSchema}
            onSubmit={onRejectStage}
          >
            <Form noValidate>
              {data?.rejection_reasons?.length > 0 && (
                <>
                  <Alert
                    status="info"
                    borderRadius="8px"
                    mb="16px"
                    bg="#DEEBFF"
                  >
                    <AlertIcon alignSelf="normal" color="#0747A6" />
                    <Text>
                      The rejection reason will be synced with your ATS and will
                      be accessible from there as well.
                    </Text>
                  </Alert>
                  <Field name="reasonId">
                    {({ form }) => (
                      <FormControl mb="12px" isRequired>
                        <FormLabel
                          fontWeight="600"
                          fontSize="14px"
                          htmlFor="reasonId"
                        >
                          Rejection reasons
                        </FormLabel>

                        <FormSelect
                          placeholder="Select"
                          options={data?.rejection_reasons}
                          fieldName="reasonId"
                        />

                        {form.errors.reasonId && (
                          <Box
                            color="#DA1E28"
                            fontSize="14px"
                            fontWeight="400px"
                            mt="6px"
                          >
                            {form.errors.reasonId}
                          </Box>
                        )}
                      </FormControl>
                    )}
                  </Field>
                </>
              )}

              <Field name="note">
                {({ field, form }) => (
                  <FormControl mb="12px">
                    <FormLabel fontWeight="600" fontSize="14px" htmlFor="note">
                      Add comments
                    </FormLabel>
                    <Textarea
                      {...field}
                      id="note"
                      name="note"
                      isInvalid={form.touched.note && form.errors.note}
                      placeholder="Write your comments here."
                    />
                    {form.touched.note && form.errors.note && (
                      <Box
                        color="#DA1E28"
                        fontSize="14px"
                        fontWeight="400px"
                        mt="6px"
                      >
                        {form.errors.note}
                      </Box>
                    )}
                  </FormControl>
                )}
              </Field>
              <ModalFooter pr="0">
                <Button variant="secondary" mr="8px" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  _hover={{ bg: '#EB4A27' }}
                  bg="#EB4A27"
                  isLoading={isLoading}
                  type="submit"
                >
                  Update
                </Button>
              </ModalFooter>
            </Form>
          </Formik>
        )}
      </ModalBody>
    </>
  );
};

const UpdateStage = ({
  candidate,
  fromStage,
  targetStage,
  onSaveSuccess,
  onClose,
}) => {
  const toast = useToast();
  const [moveStage, { isLoading }] = useMoveStageMutation();
  const onUpdateStage = async () => {
    try {
      const result = await moveStage({
        ids: [candidate?.id],
        stage: targetStage?.id,
      });
      const updateStageError = result?.data?.data?.[0]?.errors;
      if (updateStageError?.length) {
        toast({
          title: 'Contact stage update failed',
          description: updateStageError[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        onClose();
        return;
      }
      toast({
        title: 'Contact stage updated',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
      onClose();
      onSaveSuccess();
    } catch {}
  };

  return (
    <>
      <ModalHeader>
        <Text size="lg-bold">Update Stage</Text>
      </ModalHeader>
      <ModalBody>
        <SanitizedText
          size="sm"
          variant="muted"
          html={getConfirmationText({ candidate, fromStage, targetStage })}
        />
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" mr="8px" onClick={onClose}>
          Cancel
        </Button>
        <Button isLoading={isLoading} mr={3} onClick={onUpdateStage}>
          Confirm
        </Button>
      </ModalFooter>
    </>
  );
};

export const StageConfirmation = ({
  isOpen,
  onClose = () => {},
  onSaveSuccess = () => {},
  candidate,
  fromStage,
  targetStage,
}) => {
  const isRejection = rejectionStage?.includes(targetStage?.id);
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        {isRejection ? (
          <RejectStage
            candidate={candidate}
            fromStage={fromStage}
            targetStage={targetStage}
            onClose={onClose}
            onSaveSuccess={onSaveSuccess}
          />
        ) : (
          <UpdateStage
            candidate={candidate}
            fromStage={fromStage}
            targetStage={targetStage}
            onClose={onClose}
            onSaveSuccess={onSaveSuccess}
          />
        )}
      </ModalContent>
    </Modal>
  );
};
